import store from '@/store';

export interface ICartItem {
  Id: number;
  Path: string;
  Url: string;
  Data: any;
}

export class HtmlExportService {
  /**
   * Generate HTML content from cart items and return as string
   */
  static generateHtmlContent(): string {
    const groupedItems: { [key: string]: any[] } = {};
    for (const cartItem of store.state.cart as ICartItem[]) {
      const key = cartItem.Path || 'Other';
      if (!groupedItems[key]) {
        groupedItems[key] = [];
      }
      groupedItems[key].push(cartItem);
    }

    let htmlContent = `<!DOCTYPE html>
<html lang="sv-SE">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Fysisk tillgänglighet – sammanfattande riktlinjer</title>
  <meta name="description" content="Exporterade riktlinjer från kundvagnen">
  <meta name="author" content="VGR Riktlinjer">
  <style>
    * {
      margin: 0;
      padding: 0;
      box-sizing: border-box;
    }
    
    body {
      font-family: Arial, Helvetica, sans-serif;
      line-height: 1.6;
      color: #000;
      max-width: 800px;
      margin: 0 auto;
      padding: 40px 20px;
      background: #fff;
    }
    
    h1 {
      font-size: 28px;
      margin-bottom: 30px;
      color: #000;
      padding-bottom: 10px;
    }
    
    h2 {
      font-size: 22px;
      margin-top: 40px;
      margin-bottom: 20px;
      color: #000;
      padding-bottom: 8px;
    }
    
    h3 {
      font-size: 18px;
      margin-top: 30px;
      margin-bottom: 15px;
      color: #000;
    }
    
    h4 {
      font-size: 16px;
      margin-top: 20px;
      margin-bottom: 10px;
      color: #000;
    }
    
    p {
      margin-bottom: 12px;
      text-align: justify;
    }
    
    .section {
      margin-bottom: 40px;
    }
    
    .item {
      margin-bottom: 30px;
    }
    
    .guideline-item {
      margin-bottom: 20px;
    }
    
    .information {
      font-style: italic;
      color: #000;
      margin-bottom: 10px;
    }
    
    .section-link {
      font-size: 12px;
      color: #0066cc;
      margin-bottom: 15px;
      display: block;
      word-break: break-all;
    }
    
    .section-link a {
      color: #0066cc;
      text-decoration: none;
    }
    
    .section-link a:hover {
      text-decoration: underline;
    }
    
    .conditions-table .split-gron {
      color: #000;
    }
    
    .conditions-table .split-gul {
      color: #000;
    }
    .underline{
      text-decoration: underline;
      display: block;
    }
  </style>
</head>
<body>
  <main>
    <h1>Fysisk tillgänglighet – sammanfattande riktlinjer</h1>
`;

    // Process each group
    for (const [path, items] of Object.entries(groupedItems)) {
      htmlContent += `    <section class="section">\n`;
      htmlContent += `      <h2>${this.escapeHtml(path)}</h2>\n`;

      // Section link
      if (items[0].Url) {
        const baseUrl = window.location.origin;
        const fullUrl = baseUrl + items[0].Url;
        htmlContent += `      <div class="section-link"><a href="${this.escapeHtml(fullUrl)}">${this.escapeHtml(fullUrl)}</a></div>\n`;
      }

      // Process each item in the group
      for (const cartItem of items) {
        htmlContent += `      <article class="item">\n`;
        htmlContent += `        <h3>${this.escapeHtml(cartItem.Data.Name)}</h3>\n`;

        // Description
        if (cartItem.Data.Description) {
          const cleanDesc = this.stripHtml(cartItem.Data.Description);
          htmlContent += `        <p>${this.escapeHtml(cleanDesc)}</p>\n`;
        }

        // Information
        if (cartItem.Data.Information) {
          const cleanInfo = this.stripHtml(cartItem.Data.Information);
          htmlContent += `        <p class="information"><strong>Syfte:</strong> ${this.escapeHtml(cleanInfo)}</p>\n`;
        }

        // Text
        if (cartItem.Data.Text) {
          const cleanText = this.stripHtml(cartItem.Data.Text);
          htmlContent += `        <p>${this.escapeHtml(cleanText)}</p>\n`;
        }

        // Guideline items
        if (cartItem.Data.GuidelineItems && cartItem.Data.GuidelineItems.length > 0) {
          for (const item of cartItem.Data.GuidelineItems) {
            htmlContent += `        <div class="guideline-item">\n`;
            htmlContent += `          <h4>${this.escapeHtml(item.Name)}</h4>\n`;

            // Guideline item description
            if (item.Description) {
              const cleanDesc = this.stripHtml(item.Description);
              htmlContent += `          <p class="information"><strong>Information:</strong> ${this.escapeHtml(cleanDesc)}</p>\n`;
            }

            // Guideline item text
            if (item.Text) {
              const cleanText = this.stripHtml(item.Text);
              htmlContent += `          <p>${this.escapeHtml(cleanText)}</p>\n`;
            }

            // Conditions table
            const extCondition = item.Extended?.Condition ? this.stripHtml(item.Extended.Condition) : '';
            const normCondition = item.Normal?.Condition ? this.stripHtml(item.Normal.Condition) : '';

            if (extCondition || normCondition) {
              htmlContent += `          <div class="condition-container" aria-label="Förutsättningar">\n`;
              // htmlContent += `            <tbody>\n`;

              // If conditions are the same, create a single row with split cell
              if (extCondition && normCondition && extCondition === normCondition) {
                // htmlContent += `              <tr>\n`;
                // htmlContent += `                <td class="split-cell">\n`;
                // htmlContent += `                  <div class="split-cell-content">\n`;
                htmlContent += `                    <span>Gränsvärde: </span>\n`;
                // htmlContent += `                  </div>\n`;
                // htmlContent += `                </td>\n`;
                htmlContent += `                <span>${this.escapeHtml(extCondition)}</span>\n`;
                // htmlContent += `              </tr>\n`;
              } else {
                // Different conditions
                if (extCondition) {
                  // htmlContent += `              <tr>\n`;
                  htmlContent += `                <span>Gränsvärde: </span>\n`;
                  htmlContent += `                <span>${this.escapeHtml(extCondition)}</span>\n`;
                  // htmlContent += `              </tr>\n`;
                }
                if (normCondition) {
                  // htmlContent += `              <tr>\n`;
                  htmlContent += `                <span class="underline">Vid undantag</span>\n`;
                  // htmlContent += `              </tr>\n`;
                  // htmlContent += `              <tr>\n`;
                  htmlContent += `                <span>Gränsvärde: </span>\n`;
                  htmlContent += `                <span>${this.escapeHtml(normCondition)}</span>\n`;
                  // htmlContent += `              </tr>\n`;
                }
              }

              // htmlContent += `            </tbody>\n`;
              htmlContent += `          </div>\n`;
            }

            htmlContent += `        </div>\n`;
          }
        }

        htmlContent += `      </article>\n`;
      }

      htmlContent += `    </section>\n`;
    }

    htmlContent += `  </main>
</body>
</html>`;

    return htmlContent;
  }

  /**
   * Generate and download an HTML file from cart items
   */
  static async generateHtml(): Promise<void> {
    const htmlContent = this.generateHtmlContent();

    // Create and download the file
    const blob = new Blob([htmlContent], { type: 'text/html;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    
    // Generate filename with timestamp
    const now = new Date();
    const filename = `riktlinjer-export-${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}.html`;
    link.download = filename;
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  /**
   * Generate HTML and open print dialog
   */
  static printHtml(): void {
    const htmlContent = this.generateHtmlContent();
    
    // Create a hidden iframe for printing
    const iframe = document.createElement('iframe');
    iframe.style.position = 'absolute';
    iframe.style.width = '0';
    iframe.style.height = '0';
    iframe.style.border = 'none';
    
    document.body.appendChild(iframe);
    
    const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document;
    if (iframeDoc) {
      iframeDoc.open();
      iframeDoc.write(htmlContent);
      iframeDoc.close();
      
      let printExecuted = false;
      
      // Cleanup function to safely remove iframe
      const cleanup = () => {
        try {
          if (iframe && iframe.parentNode) {
            iframe.parentNode.removeChild(iframe);
          }
        } catch (e) {
          // Ignore errors if already removed
        }
      };
      
      // Wait for content to load, then print
      const printContent = () => {
        if (printExecuted) return;
        printExecuted = true;
        
        try {
          iframe.contentWindow?.focus();
          iframe.contentWindow?.print();
        } catch (e) {
          // Ignore errors
        }
        
        // Cleanup after a delay
        setTimeout(cleanup, 1000);
      };
      
      // Use iframe load event
      iframe.onload = () => {
        setTimeout(printContent, 250);
      };
    }
  }

  /**
   * Strip HTML tags from string
   */
  private static stripHtml(html: string): string {
    if (!html) return '';
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  }
  
  /**
   * Escape HTML special characters
  */
 private static escapeHtml(text: string): string {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }
}
